import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IngestionSourceAction,
  IngestionSourceStatus,
  IngestionStageType,
  OperationProblem,
} from '../../../../../../gamma-api';
import { getStageProgress } from '../../utils';
import StatusInfo from '../../../../../components/common/StatusInfo';
import { StatusStepsStyle } from '../../../../../components/common/StatusInfo/StatusSteps';
import { StatusInfoState } from '../../../../../components/common/StatusInfo/types';

import './index.less';

import { MAIN_CLASS_NAME } from './const';

const DEFAULT_ITEM = {
  title: '',
  subtitle: '',
  description: '',
  problem: undefined as OperationProblem | null | undefined,
  modal: false,
};

export interface ISourceStatusProps {
  loading: boolean;
  rolling: boolean;
  direction?: 'horizontal' | 'vertical';
  status?: IngestionSourceStatus;
  stage?: IngestionStageType | null;
  action?: IngestionSourceAction | null;
  problem?: OperationProblem | null;
  stepsStyle?: StatusStepsStyle;
  onClick?: MouseEventHandler<HTMLElement>;
  onRestart?: () => void;
}

const SourceStatus: React.FC<ISourceStatusProps> = ({
  loading,
  rolling,
  direction,
  status,
  stage,
  action,
  problem,
  stepsStyle,
  onClick,
  onRestart,
}) => {
  rolling = loading || !status || rolling;

  const { t } = useTranslation();

  const onStepsRestart = () => {
    !rolling && onRestart?.();
  };

  const getCurrent = (): number => {
    if (!status) {
      return 0;
    }

    if (stage) {
      return getStageProgress(stage);
    }

    if (
      status === IngestionSourceStatus.Empty ||
      status === IngestionSourceStatus.Pending ||
      status === IngestionSourceStatus.Preparing ||
      status === IngestionSourceStatus.Previewed
    ) {
      return 0;
    }

    return getStageProgress(undefined);
  };

  const getStatus = (): StatusInfoState => {
    if (!status) {
      return StatusInfoState.Wait;
    }

    if (
      status === IngestionSourceStatus.Empty ||
      status === IngestionSourceStatus.Pending ||
      status === IngestionSourceStatus.Preparing ||
      status === IngestionSourceStatus.Previewed
    ) {
      return StatusInfoState.Wait;
    }

    if (status == IngestionSourceStatus.Error || status == IngestionSourceStatus.Failure) {
      return StatusInfoState.Error;
    }

    if (stage) {
      return StatusInfoState.Process;
    }

    return StatusInfoState.Finish;
  };

  const items =
    action === IngestionSourceAction.Delete
      ? [
          { ...DEFAULT_ITEM, title: t('Source stage: Deletion: Loading') },
          { ...DEFAULT_ITEM, title: t('Source stage: Deletion: Extraction') },
          { ...DEFAULT_ITEM, title: t('Source stage: Deletion: Parsing') },
          { ...DEFAULT_ITEM, title: t('Source stage: Deletion: Embedding') },
          { ...DEFAULT_ITEM, title: t('Source stage: Deletion: Indexing') },
          { ...DEFAULT_ITEM, title: t('Source stage: Deletion: Deleted') },
        ]
      : [
          { ...DEFAULT_ITEM, title: t('Source stage: Ingestion: Loading') },
          { ...DEFAULT_ITEM, title: t('Source stage: Ingestion: Extraction') },
          { ...DEFAULT_ITEM, title: t('Source stage: Ingestion: Parsing') },
          { ...DEFAULT_ITEM, title: t('Source stage: Ingestion: Embedding') },
          { ...DEFAULT_ITEM, title: t('Source stage: Ingestion: Indexing') },
          { ...DEFAULT_ITEM, title: t('Source stage: Ingestion: Ingested') },
        ];

  const itemIndex = getCurrent();
  const itemStatus = getStatus();

  if (itemStatus === StatusInfoState.Error) {
    const item = items[itemIndex];
    item.subtitle = t('Source stage: Problem: Subtitle');
    item.description = t('Source stage: Problem: Description');
    item.problem = problem;
    item.modal = true;
  }

  if (direction !== 'vertical') {
    return (
      <StatusInfo.Steps
        className={MAIN_CLASS_NAME}
        current={itemIndex}
        direction="horizontal"
        items={items}
        rolling={rolling}
        status={itemStatus}
        style={stepsStyle}
        onRestart={onStepsRestart}
      />
    );
  }

  const onDropdownClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
  };

  return (
    <StatusInfo.Dropdown
      className={MAIN_CLASS_NAME}
      current={getCurrent()}
      items={items}
      rolling={rolling}
      status={getStatus()}
      onClick={onDropdownClick}
      onRestart={onStepsRestart}
    />
  );
};

export default SourceStatus;
